// src/components/VersionFooter.js
// =============================================================================
// Shared version footer line for both portals.
// Displays a subtle version indicator at the very bottom of each page,
// along with Privacy Policy and Terms links that open an InfoModal.
// =============================================================================

/**
 * @fileoverview Shared version footer component for Red Setter Homes Portal.
 * Renders a small, unobtrusive version line at the bottom of portal pages,
 * with links to view the Privacy Policy and Terms & Conditions in a modal.
 * Used by both client and admin portals with their respective version strings.
 * @module components/VersionFooter
 */

import React, { useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import InfoModal from "./InfoModal";
import { PRIVACY_POLICY_CONTENT, TERMS_CONTENT } from "./legalContent";
import "./VersionFooter.css";

/**
 * Renders a subtle version indicator footer line with legal links.
 *
 * @param {Object} props
 * @param {string} props.version - The version string to display (e.g. "v1.0.47")
 * @param {'client' | 'admin'} [props.portal='client'] - Which portal this is for (affects label)
 * @returns {React.ReactElement} The version footer element
 *
 * @example
 * <VersionFooter version={getClientVersion()} portal="client" />
 */
function VersionFooter({ version, portal = "client" }) {
  const label = portal === "admin" ? "Admin Portal" : "Client Portal";

  /** @type {[null | 'privacy' | 'terms', Function]} */
  const [activeModal, setActiveModal] = useState(null);

  /** @type {() => void} */
  const closeModal = () => setActiveModal(null);

  const isPrivacy = activeModal === "privacy";
  const modalTitle = isPrivacy ? "Privacy Policy" : "Terms & Conditions";
  const modalContent = isPrivacy ? PRIVACY_POLICY_CONTENT : TERMS_CONTENT;

  return (
    <>
      <div className="version-footer">
        <span className="version-footer-text">
          Red Setter Homes {" · "} {label} {version}
          {" · "}
          <span
            className="vf-legal-link"
            role="button"
            tabIndex={0}
            onClick={() => setActiveModal("privacy")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActiveModal("privacy");
              }
            }}
          >
            Privacy Policy
          </span>
          {" · "}
          <span
            className="vf-legal-link"
            role="button"
            tabIndex={0}
            onClick={() => setActiveModal("terms")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActiveModal("terms");
              }
            }}
          >
            Terms and Conditions
          </span>
        </span>
      </div>

      <InfoModal
        isOpen={activeModal !== null}
        onClose={closeModal}
        title={modalTitle}
        icon={<FaShieldAlt />}
      >
        {modalContent}
      </InfoModal>
    </>
  );
}

export default VersionFooter;
